import { useInfiniteQuery, useMutation, useQuery } from "@tanstack/react-query";
import {
  getOrgProfile,
  getOrgSurgeons,
  getOrgTemplateById,
  getOrgTemplates,
} from "../dataAccess/apiService";
import { useBoundStore } from "../stores/useBoundStore";
import { useEffect } from "react";
import { useAuth } from "react-oidc-context";
import { fetchJsonData } from "../helpers";

export function useGetOrg(ptEnrollmentOrgId?: string) {
  const selectedOrgId = useBoundStore((state) => state.selectedOrgId);
  const auth = useAuth();

  const orgId = ptEnrollmentOrgId ? ptEnrollmentOrgId : selectedOrgId;
  const setOrgProfile = useBoundStore((state) => state.setOrgProfile);

  const orgProfileQuery = useQuery({
    queryKey: ["get-org-profile", orgId],
    queryFn: () => {
      if (!orgId) {
        return Promise.reject(new Error("Missing orgId"));
      }
      return getOrgProfile(orgId);
    },
    enabled: !!orgId && auth.isAuthenticated,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
  });

  useEffect(() => {
    if (orgProfileQuery.data) {
      setOrgProfile(orgProfileQuery.data);
    }
  }, [orgProfileQuery.data, setOrgProfile]);

  return orgProfileQuery;
}

export const useGetOrgSurgeons = () => {
  const selectedOrgId = useBoundStore((state) => state.selectedOrgId);
  const setOrgSurgeons = useBoundStore((state) => state.setOrgSurgeons);
  const setIsFilterInitialized = useBoundStore(
    (state) => state.setIsFilterInitialized
  );

  const orgSurgeonsQuery = useInfiniteQuery({
    queryKey: ["get-org-surgeons", selectedOrgId],
    queryFn: ({ pageParam }) => {
      if (!selectedOrgId) {
        return Promise.reject(new Error("Missing orgId"));
      }
      return getOrgSurgeons(selectedOrgId, pageParam);
    },
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => lastPage?.cursor ?? undefined,
    enabled: !!selectedOrgId,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
  });

  useEffect(() => {
    setIsFilterInitialized(false);
  }, [selectedOrgId, setIsFilterInitialized]);

  useEffect(() => {
    if (orgSurgeonsQuery.data) {
      const orgSurgeons =
        orgSurgeonsQuery.data?.pages.flatMap((page) => page?.data ?? []) ?? [];
      setOrgSurgeons(orgSurgeons);
    }
  }, [orgSurgeonsQuery.data, setOrgSurgeons]);

  return orgSurgeonsQuery;
};

export const useGetOrgTemplates = () => {
  const selectedOrgId = useBoundStore((state) => state.selectedOrgId);

  const orgTemplatesQuery = useQuery({
    queryKey: ["get-org-templates", selectedOrgId],
    queryFn: () => {
      if (!selectedOrgId) {
        return Promise.reject(new Error("Missing orgId"));
      }
      return getOrgTemplates(selectedOrgId);
    },
    enabled: !!selectedOrgId,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
  });

  return orgTemplatesQuery;
};

export const useGetTemplateById = () => {
  const selectedOrgId = useBoundStore((state) => state.selectedOrgId);
  const templateId = useBoundStore((state) => state.currentCarePlanId);
  const setCurrentCarePlan = useBoundStore((state) => state.setCurrentCarePlan);

  const orgTemplateQuery = useQuery({
    queryKey: ["get-org-template", selectedOrgId, templateId],
    queryFn: () => {
      if (selectedOrgId && templateId) {
        return getOrgTemplateById(selectedOrgId, templateId);
      }
    },
    enabled: !!selectedOrgId && !!templateId,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
  });

  const careplanS3DataQuery = useQuery({
    queryKey: ["get-careplan-s3-data", orgTemplateQuery.data?.s3_url],
    queryFn: async () => {
      if (!orgTemplateQuery.data?.s3_url) {
        throw new Error("No S3 URL available");
      }
      return await fetchJsonData(orgTemplateQuery.data.s3_url);
    },
    enabled: !!orgTemplateQuery.data?.s3_url,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
  });

  useEffect(() => {
    if (careplanS3DataQuery.data) {
      setCurrentCarePlan(careplanS3DataQuery.data);
    }
  }, [careplanS3DataQuery.data, setCurrentCarePlan]);

  return { orgTemplateQuery, careplanS3DataQuery };
};
